import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CpmButton } from '../../../../components/design-system';
import {
    CPM_LICENSE_TYPE_CODE_MAX_LENGTH,
    CPM_LICENSE_TYPE_LABEL_MAX_LENGTH,
    CPM_LICENSE_TYPE_MAX_COUNT,
    CPM_LICENSE_TYPES_COPY,
    cpmLicenseTypeDuplicateIssue,
    cpmLicenseTypeEmptyCodeIssue,
    cpmLicenseTypesHeaderNote,
    cpmLicenseTypesSavedToast,
} from '../../../../constants/centralProductMaster.constants';
import { updateStateLicenseTypes } from '../../../../services/productMasterService';
import type { CpmLicenseType, CpmState } from '../../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import { toast } from '../../../../utils/toast';

interface LicenseTypesPanelProps {
    state: CpmState;
    onSaved: () => void;
}

type DraftRow = {
    key: string;
    code: string;
    label: string;
};

const ROSTER_SCROLL_AFTER = 26;
const ROSTER_MAX_HEIGHT = 170;

let rowSeq = 0;

function nextRowKey(): string {
    rowSeq += 1;
    return `lt-${rowSeq}`;
}

function cloneDraft(types: CpmLicenseType[] | undefined): DraftRow[] {
    return (types ?? []).map((t) => ({
        key: nextRowKey(),
        code: t.code,
        label: t.label,
    }));
}

function normalize(rows: DraftRow[]): CpmLicenseType[] {
    return rows
        .map((r) => {
            const code = r.code.trim();
            const label = r.label.trim() || code;
            return { code, label };
        })
        .filter((r) => r.code.length > 0);
}

function payloadKey(rows: CpmLicenseType[]): string {
    return JSON.stringify(rows);
}

function PlusIcon({ size }: { size: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={size <= 12 ? 2.6 : 2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
        </svg>
    );
}

function LicenseTypeWhyTip() {
    const [open, setOpen] = useState(false);

    return (
        <span
            className="relative inline-flex items-center justify-center shrink-0 rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
            aria-label={CPM_LICENSE_TYPES_COPY.WHY_TITLE}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen(true)}
            style={{ width: 17, height: 17, cursor: 'default', flex: 'none' }}
        >
            <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
            </svg>
            {open ? (
                <span
                    role="tooltip"
                    className="pointer-events-none absolute z-40 flex flex-col text-left"
                    style={{
                        top: 'calc(100% + 10px)',
                        left: -12,
                        width: 290,
                        gap: 6,
                        padding: '12px 13px',
                        borderRadius: 12,
                        background: '#fff',
                        border: '1px solid var(--cpm-border-strong)',
                        boxShadow: 'var(--cpm-shadow-popover)',
                        letterSpacing: 0,
                    }}
                >
                    <span
                        className="uppercase text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.07em' }}
                    >
                        {CPM_LICENSE_TYPES_COPY.WHY_TITLE}
                    </span>
                    <span
                        className="text-[var(--cpm-text-primary)]"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            textWrap: 'pretty',
                        }}
                    >
                        {CPM_LICENSE_TYPES_COPY.WHY_BODY}
                    </span>
                    <span
                        className="text-[var(--cpm-text-tertiary)]"
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1.45,
                            textWrap: 'pretty',
                        }}
                    >
                        {CPM_LICENSE_TYPES_COPY.WHY_FOOTNOTE}
                    </span>
                </span>
            ) : null}
        </span>
    );
}

export default function LicenseTypesPanel({ state, onSaved }: LicenseTypesPanelProps) {
    const saved = useMemo(() => state.licenseTypes ?? [], [state.licenseTypes]);
    const savedKey = useMemo(() => payloadKey(saved), [saved]);

    const [draft, setDraft] = useState<DraftRow[]>(() => cloneDraft(state.licenseTypes));
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [committedKey, setCommittedKey] = useState<string | null>(null);
    const [popAlignEnd, setPopAlignEnd] = useState(false);
    const rosterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setDraft(cloneDraft(saved));
        setSaveError(null);
        setEditingKey(null);
        setCommittedKey(null);
        // savedKey is the content identity — `saved` would change on every listStates refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state._id, savedKey]);

    useEffect(() => {
        if (!editingKey) return;
        const onAway = (e: MouseEvent) => {
            const t = e.target as HTMLElement | null;
            if (t?.closest?.('[data-lic-chip]')) return;
            setEditingKey(null);
        };
        document.addEventListener('mousedown', onAway, true);
        return () => document.removeEventListener('mousedown', onAway, true);
    }, [editingKey]);

    useLayoutEffect(() => {
        if (!editingKey) return;
        const chip = rosterRef.current?.querySelector(`[data-lic-chip="${editingKey}"]`);
        const roster = rosterRef.current;
        if (!chip || !roster) return;
        const cr = chip.getBoundingClientRect();
        const rr = roster.getBoundingClientRect();
        setPopAlignEnd(cr.left - rr.left + 290 > rr.width);
    }, [editingKey, draft.length]);

    const payload = useMemo(() => normalize(draft), [draft]);
    const payloadSerialized = payloadKey(payload);
    const unchanged = payloadSerialized === savedKey || payloadSerialized === committedKey;
    const dirty = !unchanged && !saving;
    const blankRow = draft.some((r) => !r.code.trim() && !r.label.trim());

    const duplicateCodes = useMemo(() => {
        const seen = new Set<string>();
        const dups = new Set<string>();
        let firstDupDisplay: string | null = null;
        for (const r of draft) {
            const k = r.code.trim().toLowerCase();
            if (!k) continue;
            if (seen.has(k)) {
                dups.add(k);
                if (!firstDupDisplay) {
                    const first = draft.find((x) => x.code.trim().toLowerCase() === k);
                    firstDupDisplay = first?.code.trim() || r.code.trim();
                }
            }
            seen.add(k);
        }
        return { dups, firstDupDisplay };
    }, [draft]);

    const issue = useMemo(() => {
        if (duplicateCodes.dups.size > 0 && duplicateCodes.firstDupDisplay) {
            return cpmLicenseTypeDuplicateIssue(duplicateCodes.firstDupDisplay);
        }
        const orphan = draft.find((r) => !r.code.trim() && r.label.trim());
        if (orphan) return cpmLicenseTypeEmptyCodeIssue(orphan.label.trim());
        if (payload.length > CPM_LICENSE_TYPE_MAX_COUNT) return CPM_LICENSE_TYPES_COPY.ISSUE_MAX;
        return null;
    }, [draft, duplicateCodes, payload.length]);

    const canAdd = !saving && draft.length < CPM_LICENSE_TYPE_MAX_COUNT;
    const canSave = dirty && !issue && !saving;
    const typeCount = payload.length;
    const countLabel = `${typeCount} ${typeCount === 1 ? 'type' : 'types'}`;
    const errText = issue ?? saveError;
    const hasRows = draft.length > 0;
    const hint = saving
        ? ''
        : issue
          ? ''
          : dirty
            ? CPM_LICENSE_TYPES_COPY.UNSAVED
            : blankRow
              ? CPM_LICENSE_TYPES_COPY.HINT_BLANK
              : '';

    const updateRow = (key: string, patch: Partial<Pick<DraftRow, 'code' | 'label'>>) => {
        setDraft((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
        setSaveError(null);
    };

    const handleAdd = () => {
        if (!canAdd) return;
        const key = nextRowKey();
        setDraft((rows) => [...rows, { key, code: '', label: '' }]);
        setEditingKey(key);
        setSaveError(null);
    };

    const handleDelete = (key: string) => {
        if (saving) return;
        setDraft((rows) => rows.filter((r) => r.key !== key));
        setEditingKey(null);
        setSaveError(null);
    };

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setSaveError(null);
        try {
            await updateStateLicenseTypes({
                state: state.name,
                licenseTypes: payload,
            });
            setCommittedKey(payloadSerialized);
            setEditingKey(null);
            toast.success(cpmLicenseTypesSavedToast(state.name, payload.length));
            onSaved();
        } catch (err) {
            const msg = getApiErrorMessage(err);
            setSaveError(msg);
            if (msg.includes('still use it')) {
                setDraft(cloneDraft(saved));
                setCommittedKey(null);
            }
        } finally {
            setSaving(false);
        }
    };

    const addChip = (
        <button
            type="button"
            disabled={!canAdd}
            onClick={handleAdd}
            className="inline-flex items-center shrink-0 bg-white text-[var(--cpm-text-secondary)] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-purple)] disabled:cursor-not-allowed focus-visible:!shadow-none"
            style={{
                gap: 6,
                height: 30,
                padding: '0 12px',
                borderRadius: 999,
                flex: 'none',
                cursor: canAdd ? 'pointer' : 'not-allowed',
                opacity: saving ? 0.5 : 1,
                fontSize: 11.5,
                fontWeight: 800,
                boxShadow: 'none',
            }}
        >
            <PlusIcon size={12} />
            {CPM_LICENSE_TYPES_COPY.ADD}
        </button>
    );

    return (
        <div
            className={`rounded-[12px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)] flex flex-col min-w-0 overflow-visible ${
                editingKey ? 'relative z-20' : ''
            }`}
            style={{
                gap: 11,
                padding: '14px 16px 15px',
                boxShadow: '0 1px 2px rgba(14,8,88,.04)',
            }}
        >
            <div className="flex items-center flex-wrap" style={{ gap: 9 }}>
                <span
                    className="uppercase text-[var(--cpm-brand-navy)] shrink-0"
                    style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    {CPM_LICENSE_TYPES_COPY.TITLE}
                </span>
                <span
                    className="inline-flex items-center shrink-0 rounded-full tabular"
                    style={{
                        padding: '3px 9px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                        background: typeCount
                            ? 'var(--cpm-purple-tint-7)'
                            : 'var(--cpm-surface-subtle)',
                        border: `1px solid ${
                            typeCount
                                ? 'var(--cpm-purple-accent-border)'
                                : 'var(--cpm-border-default)'
                        }`,
                        color: typeCount
                            ? 'var(--cpm-brand-navy)'
                            : 'var(--cpm-text-tertiary)',
                    }}
                >
                    {countLabel}
                </span>
                <LicenseTypeWhyTip />
                <span
                    className="ml-auto text-[var(--cpm-text-tertiary)]"
                    style={{ fontSize: 11.5, fontWeight: 700, textWrap: 'pretty' }}
                >
                    {cpmLicenseTypesHeaderNote(state.name)}
                </span>
            </div>

            {hasRows ? (
                <div
                    ref={rosterRef}
                    className="flex flex-wrap items-center min-w-0"
                    style={{
                        gap: 7,
                        maxHeight: draft.length > ROSTER_SCROLL_AFTER ? ROSTER_MAX_HEIGHT : undefined,
                        overflowY: draft.length > ROSTER_SCROLL_AFTER ? 'auto' : undefined,
                        paddingRight: draft.length > ROSTER_SCROLL_AFTER ? 8 : undefined,
                    }}
                >
                    {draft.map((row) => {
                        const editing = editingKey === row.key;
                        const codeVal = row.code.trim();
                        const dupHere =
                            !!codeVal && duplicateCodes.dups.has(codeVal.toLowerCase());
                        const bad = dupHere || (!codeVal && !!row.label.trim());
                        const blank = !codeVal;
                        return (
                            <span
                                key={row.key}
                                data-lic-chip={row.key}
                                className="relative shrink-0"
                                style={{ flex: 'none' }}
                            >
                                <span
                                    role="button"
                                    tabIndex={saving ? -1 : 0}
                                    title={CPM_LICENSE_TYPES_COPY.EDIT_TITLE}
                                    onClick={() => {
                                        if (!saving) setEditingKey(row.key);
                                    }}
                                    onKeyDown={(e) => {
                                        if (saving) return;
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setEditingKey(row.key);
                                        }
                                    }}
                                    className="inline-flex items-center focus-visible:!shadow-none"
                                    style={{
                                        gap: 8,
                                        height: 30,
                                        padding: '0 11px',
                                        borderRadius: 999,
                                        transition: 'all 150ms ease',
                                        cursor: saving ? 'default' : 'pointer',
                                        boxShadow: 'none',
                                        background: editing
                                            ? 'var(--cpm-purple-tint-7)'
                                            : bad
                                              ? 'var(--cpm-shortfall-bg)'
                                              : '#fff',
                                        border: `1.5px solid ${
                                            editing
                                                ? 'var(--cpm-brand-purple)'
                                                : bad
                                                  ? 'var(--cpm-shortfall-border)'
                                                  : blank
                                                    ? 'var(--cpm-warning-border)'
                                                    : 'var(--cpm-border-strong)'
                                        }`,
                                    }}
                                >
                                    <span
                                        className="text-[var(--cpm-brand-navy)]"
                                        style={{
                                            fontFamily: 'var(--cpm-font-mono)',
                                            fontSize: 11.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.02em',
                                        }}
                                    >
                                        {codeVal || CPM_LICENSE_TYPES_COPY.CHIP_NEW_CODE}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            maxWidth: 168,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: row.label.trim()
                                                ? 'var(--cpm-text-secondary)'
                                                : 'var(--cpm-text-tertiary)',
                                        }}
                                    >
                                        {row.label.trim() ||
                                            (codeVal
                                                ? CPM_LICENSE_TYPES_COPY.CHIP_LABEL_EQ_CODE
                                                : CPM_LICENSE_TYPES_COPY.CHIP_ADD_LABEL)}
                                    </span>
                                </span>
                                {editing ? (
                                    <span
                                        className="cpm-animate-pop-in absolute z-[45] flex"
                                        style={{
                                            top: 'calc(100% + 7px)',
                                            left: popAlignEnd ? undefined : 0,
                                            right: popAlignEnd ? 0 : undefined,
                                            width: 290,
                                        }}
                                    >
                                        <span
                                            className="flex flex-col w-full box-border bg-white"
                                            style={{
                                                gap: 9,
                                                padding: 13,
                                                borderRadius: 12,
                                                border: '1px solid var(--cpm-border-strong)',
                                                boxShadow: 'var(--cpm-shadow-popover)',
                                            }}
                                        >
                                            <span className="flex flex-col" style={{ gap: 5 }}>
                                                <span
                                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                                    style={{
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.07em',
                                                    }}
                                                >
                                                    {CPM_LICENSE_TYPES_COPY.CODE_FIELD}
                                                </span>
                                                <input
                                                    value={row.code}
                                                    onChange={(e) =>
                                                        updateRow(row.key, { code: e.target.value })
                                                    }
                                                    maxLength={CPM_LICENSE_TYPE_CODE_MAX_LENGTH}
                                                    placeholder={
                                                        CPM_LICENSE_TYPES_COPY.CODE_PLACEHOLDER
                                                    }
                                                    disabled={saving}
                                                    autoFocus
                                                    autoComplete="off"
                                                    spellCheck={false}
                                                    className="box-border outline-none bg-white text-[var(--cpm-brand-navy)] placeholder:text-[var(--cpm-text-tertiary)]"
                                                    style={{
                                                        height: 32,
                                                        padding: '0 10px',
                                                        borderRadius: 9,
                                                        border: '1.5px solid var(--cpm-border-strong)',
                                                        fontFamily: 'var(--cpm-font-mono)',
                                                        fontSize: 12.5,
                                                        fontWeight: 700,
                                                        letterSpacing: '0.02em',
                                                    }}
                                                />
                                            </span>
                                            <span className="flex flex-col" style={{ gap: 5 }}>
                                                <span
                                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                                    style={{
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.07em',
                                                    }}
                                                >
                                                    {CPM_LICENSE_TYPES_COPY.LABEL_FIELD}
                                                </span>
                                                <input
                                                    value={row.label}
                                                    onChange={(e) =>
                                                        updateRow(row.key, {
                                                            label: e.target.value,
                                                        })
                                                    }
                                                    maxLength={CPM_LICENSE_TYPE_LABEL_MAX_LENGTH}
                                                    placeholder={
                                                        CPM_LICENSE_TYPES_COPY.LABEL_PLACEHOLDER
                                                    }
                                                    disabled={saving}
                                                    autoComplete="off"
                                                    className="box-border outline-none text-[var(--cpm-text-primary)] placeholder:text-[var(--cpm-text-tertiary)]"
                                                    style={{
                                                        height: 32,
                                                        padding: '0 10px',
                                                        borderRadius: 9,
                                                        border: '1.5px solid var(--cpm-border-default)',
                                                        background: 'var(--cpm-surface-subtle)',
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            </span>
                                            <span
                                                className="flex items-center"
                                                style={{ gap: 8, paddingTop: 2 }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(row.key)}
                                                    className="inline-flex items-center bg-white text-[var(--cpm-text-secondary)] border-[1.5px] border-solid border-[var(--cpm-border-strong)] hover:border-[var(--cpm-shortfall-text)] hover:text-[var(--cpm-shortfall-text)] focus-visible:!shadow-none"
                                                    style={{
                                                        gap: 6,
                                                        height: 30,
                                                        padding: '0 11px',
                                                        borderRadius: 9,
                                                        cursor: 'pointer',
                                                        fontSize: 11.5,
                                                        fontWeight: 800,
                                                        boxShadow: 'none',
                                                    }}
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M18 6 6 18" />
                                                        <path d="m6 6 12 12" />
                                                    </svg>
                                                    {CPM_LICENSE_TYPES_COPY.REMOVE}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingKey(null)}
                                                    className="ml-auto inline-flex items-center bg-[var(--cpm-brand-navy)] text-white focus-visible:!shadow-none"
                                                    style={{
                                                        height: 30,
                                                        padding: '0 13px',
                                                        borderRadius: 9,
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: 11.5,
                                                        fontWeight: 800,
                                                        boxShadow: 'none',
                                                    }}
                                                >
                                                    {CPM_LICENSE_TYPES_COPY.DONE}
                                                </button>
                                            </span>
                                        </span>
                                    </span>
                                ) : null}
                            </span>
                        );
                    })}
                    {addChip}
                </div>
            ) : (
                <div
                    className="flex items-center flex-wrap bg-[var(--cpm-surface-subtle)]"
                    style={{
                        gap: 16,
                        padding: '13px 14px',
                        borderRadius: 10,
                        border: '1.5px dashed var(--cpm-border-strong)',
                    }}
                >
                    <span
                        className="flex-1 text-[var(--cpm-text-secondary)]"
                        style={{
                            minWidth: 260,
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            textWrap: 'pretty',
                        }}
                    >
                        {CPM_LICENSE_TYPES_COPY.EMPTY}
                    </span>
                    <button
                        type="button"
                        disabled={!canAdd}
                        onClick={handleAdd}
                        className="inline-flex items-center shrink-0 bg-white text-[var(--cpm-text-secondary)] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-purple)] disabled:cursor-not-allowed focus-visible:!shadow-none"
                        style={{
                            alignSelf: 'flex-start',
                            gap: 7,
                            height: 32,
                            padding: '0 12px',
                            borderRadius: 10,
                            cursor: canAdd ? 'pointer' : 'not-allowed',
                            opacity: saving ? 0.5 : 1,
                            fontSize: 12,
                            fontWeight: 800,
                            boxShadow: 'none',
                        }}
                    >
                        <PlusIcon size={13} />
                        {CPM_LICENSE_TYPES_COPY.ADD}
                    </button>
                </div>
            )}

            {errText ? (
                <div
                    className="flex items-start bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                    style={{ gap: 9, padding: '10px 13px', borderRadius: 10 }}
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-shortfall-text)"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        style={{ marginTop: 1 }}
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                    <span
                        className="flex-1 text-[var(--cpm-shortfall-text)]"
                        style={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            lineHeight: 1.45,
                            textWrap: 'pretty',
                        }}
                    >
                        {errText}
                    </span>
                    {saveError && !issue ? (
                        <button
                            type="button"
                            onClick={() => setSaveError(null)}
                            className="shrink-0 inline-flex items-center bg-white text-[var(--cpm-shortfall-text)]"
                            style={{
                                height: 26,
                                padding: '0 10px',
                                borderRadius: 8,
                                border: '1.5px solid var(--cpm-shortfall-border)',
                                cursor: 'pointer',
                                fontSize: 11.5,
                                fontWeight: 800,
                            }}
                        >
                            {CPM_LICENSE_TYPES_COPY.DISMISS}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {hasRows || dirty ? (
                <div
                    className="flex items-center flex-wrap"
                    style={{
                        gap: 12,
                        paddingTop: 11,
                        borderTop: '1px solid var(--cpm-border-default)',
                    }}
                >
                    {hasRows ? (
                        <span
                            className="text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11.5, fontWeight: 700 }}
                        >
                            {CPM_LICENSE_TYPES_COPY.FOOTER_HELP}
                        </span>
                    ) : null}
                    <span
                        className="ml-auto"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 800,
                            color: dirty
                                ? 'var(--cpm-brand-purple)'
                                : 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {hint}
                    </span>
                    <CpmButton
                        variant="navy"
                        size="sm"
                        disabled={!canSave && !saving}
                        aria-busy={saving}
                        onClick={() => {
                            void handleSave();
                        }}
                        className="hover:!translate-y-0 hover:!brightness-[1.08] !tracking-normal disabled:!opacity-[.45]"
                        style={{
                            height: 34,
                            paddingLeft: 15,
                            paddingRight: 15,
                            gap: 8,
                            letterSpacing: 0,
                            opacity: canSave || saving ? 1 : 0.45,
                            boxShadow: canSave ? '0 8px 20px -6px rgba(14,8,88,.60)' : 'none',
                            cursor: canSave ? 'pointer' : 'not-allowed',
                        }}
                        leadingIcon={
                            saving ? (
                                <span className="cpm-spin-w" style={{ width: 13, height: 13 }} />
                            ) : undefined
                        }
                    >
                        {saving ? CPM_LICENSE_TYPES_COPY.SAVING : CPM_LICENSE_TYPES_COPY.SAVE}
                    </CpmButton>
                </div>
            ) : null}
        </div>
    );
}
